import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { AbstractControl, FormArray, FormBuilder, FormControl, FormGroup, ValidationErrors, Validators } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { ClassificationService } from 'src/app/core/services/api/classification.service';
import { PlatformService } from 'src/app/core/services/api/platform.service';

import { forkJoin, Observable, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { Swal2 } from 'src/app/core/utils/swal2.util';
import { PlatformMedia } from 'src/app/core/contracts/models/medio-plataformas';
import { TenantLocalService } from 'src/app/core/services/api/tenantlocal.service';

/** Valor mayor a 0: en el resto del sistema un 0 en valor o alcance equivale a "sin llenar" */
function greaterThanZero(control: AbstractControl): ValidationErrors | null {
    const value = control.value;
    if (value === null || value === '') return null; // de lo vacío se encarga Validators.required
    return Number(value) > 0 ? null : { greaterThanZero: true };
}

type PlatformEntryForm = FormGroup<{
    platform: FormControl<number | null>;
    classification: FormControl<number | null>;
    url: FormControl<string | null>;
    scope: FormControl<number | null>;
    valor: FormControl<number | null>;
    observation: FormControl<string | null>;
}>;

@Component({
    selector: 'app-asignar-plataformas-form',
    templateUrl: './asignar-plataformas-form.component.html',
    styleUrls: ['./asignar-plataformas-form.component.scss']
})
export class AsignarPlataformasFormComponent implements OnInit, OnDestroy {
    public loading: boolean = false;
    public loadingCatalogs: boolean = true;

    entries = new FormArray<PlatformEntryForm>([]);

    platforms: any[] = [];
    classificationsTemp: any[] = [];

    title = 'Agregar plataformas';

    /** Configuración adicional del tenant: "Hacer obligatorio la valorización y alcance de impacto" */
    readonly valorizacionObligatoria: boolean = Boolean(this.tenantLocal.tenant?.valorizacion_alcance);

    private destroy$ = new Subject<void>();

    constructor(
        private formBuilder: FormBuilder,
        public dialogRef: MatDialogRef<AsignarPlataformasFormComponent>,
        @Inject(MAT_DIALOG_DATA) public data: { row: PlatformMedia, id: number },
        private platformService: PlatformService,
        private classificationService: ClassificationService,
        private swal: Swal2,
        private tenantLocal: TenantLocalService
    ) {
        if (data.row) {
            this.title = 'Editar plataforma';
        }
    }

    get isEdit(): boolean {
        return !!this.data.row;
    }

    ngOnInit(): void {
        this.addEntry();

        // Plataformas y clasificaciones se guardan en memoria: solo la primera apertura va al servidor.
        forkJoin([
            this.platformService.getAllCached(),
            this.classificationService.getAllCached()
        ]).pipe(takeUntil(this.destroy$)).subscribe({
            next: ([platformsResponse, classificationsResponse]: any[]) => {
                this.platforms = platformsResponse.plataformas;
                this.classificationsTemp = classificationsResponse.plataformaClasificacions;
                this.loadingCatalogs = false;
                if (this.isEdit) {
                    this.loadForm();
                }
            },
            error: () => this.loadingCatalogs = false
        });
    }

    ngOnDestroy() {
        this.destroy$.next();
        this.destroy$.complete();
    }

    addEntry(): void {
        const entry: PlatformEntryForm = this.formBuilder.group({
            platform: new FormControl<number | null>(null, [Validators.required]),
            classification: new FormControl<number | null>({ value: null, disabled: true }, [Validators.required]),
            url: new FormControl<string | null>(null, [Validators.required]),
            scope: new FormControl<number | null>(null, this.valorizacionValidators),
            valor: new FormControl<number | null>(null, this.valorizacionValidators),
            observation: new FormControl<string | null>(null)
        });

        entry.controls.platform.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(id => {
            entry.controls.classification.setValue(null);
            if (id) {
                entry.controls.classification.enable();
            } else {
                entry.controls.classification.disable();
            }
        });

        this.entries.push(entry);
    }

    private get valorizacionValidators() {
        return this.valorizacionObligatoria ? [Validators.required, greaterThanZero] : [];
    }

    removeEntry(index: number): void {
        if (this.entries.length > 1) {
            this.entries.removeAt(index);
        }
    }

    classificationsFor(entry: PlatformEntryForm): any[] {
        const id = entry.controls.platform.value;
        return id ? this.classificationsTemp.filter(c => c.plataforma_id === id) : [];
    }

    tipoValorizacionFor(entry: PlatformEntryForm): string | null {
        const id = entry.controls.platform.value;
        return this.platforms.find(p => p.id === id)?.tipo_valorizacion ?? null;
    }

    loadForm() {
        const row: any = this.data.row;
        const entry = this.entries.at(0);
        entry.controls.platform.setValue(row.plataforma_clasificacion.plataforma.id);
        entry.controls.classification.setValue(row.plataforma_clasificacion.id);
        entry.controls.url.setValue(row.valor);
        entry.controls.scope.setValue(row.alcance);
        entry.controls.observation.setValue(row.observacion);
        entry.controls.valor.setValue(Number(row.valorizacion_plataforma));
    }

    private toPlatformMedia(entry: PlatformEntryForm): PlatformMedia {
        const value = entry.getRawValue();
        return {
            medio_id: this.data.id,
            alcance: value.scope,
            idPlataformaClasificacion: value.classification,
            valor: value.url,
            observacion: value.observation,
            valorizacion: value.valor,
        };
    }

    onSubmit() {
        let observable: Observable<any>;
        if (this.isEdit) {
            const platform = this.toPlatformMedia(this.entries.at(0));
            platform.id = this.data.row.id;
            observable = this.platformService.updateMedioPlataformas(platform);
        } else {
            const platforms = this.entries.controls.map(entry => this.toPlatformMedia(entry));
            observable = this.platformService.createManyMedioPlataformas(this.data.id, platforms);
        }
        this.loading = true;
        observable.subscribe((response: any) => {
            this.dialogRef.close(response);
            this.loading = false;
            this.swal.showToast(response.message, 'success');
        }, error => {
            if (error.status === 400) {
                this.error(error);
            }
            this.loading = false;
        });
    }

    error(error) {
        const errors = error.error?.errors;
        const first = errors ? Object.values(errors)[0] as string[] : null;
        this.swal.showToast(first?.[0] ?? error.error?.message ?? 'Los datos enviados no son correctos', 'error');
    }

}
